import { useState, useEffect } from 'react'
import { useApp } from '../context/AppContext'
import { useTheme } from '../hooks/useTheme'
import { useAppUpdate, CHECK_RESULT_EVENT } from '../components/UpdateDialog'
import { userApi } from '../services/api.js'

export default function ProfilePage() {
  const { navigate, showToast, user, isLoggedIn, logout, fetchProfile, favorites } = useApp()

  const MENU_GROUPS = [
    [
      { icon: 'fa-wallet',        color: '#6C63FF', label: '我的钱包',  toPage: 'wallet' },
      { icon: 'fa-heart',         color: '#FF6B6B', label: '我的收藏',  toPage: 'favorites', regular: true, count: favorites?.length || 0 },
      { icon: 'fa-certificate',   color: '#F9CA24', label: '学习证书',  toPage: 'certificates', badge: '1' },
      { icon: 'fa-note-sticky',   color: '#20BF6B', label: '我的笔记',  toPage: 'notes', regular: true },
    ],
    [
      { icon: 'fa-chart-pie',    color: '#6C63FF', label: '学习仪表盘', toPage: 'dashboard' },
      { icon: 'fa-user-plus',    color: '#0ABDE3', label: '邀请好友',  toPage: 'invite', tag: '得奖励' },
      { icon: 'fa-circle-question', color: '#A29BFE', label: '帮助中心', toPage: 'help' },
      { icon: 'fa-arrow-up-from-bracket', color: '#00B894', label: '检查更新', action: '__check_update__' },
      { icon: 'fa-gear',         color: 'inherit', label: '设置',      toPage: 'settings' },
    ],
  ]

  const { bgPrimary, bgWhite, textPrimary, textSecondary, textMuted, borderColorLight, shadowColor, progressBg, isDark } = useTheme()
  const { hasUpdate, checking, checkAndShow, currentVersion } = useAppUpdate()

  // 用户资料编辑
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [saving, setSaving] = useState(false)

  const startEdit = () => {
    setEditName(user?.name || '')
    setEditing(true)
  }

  const saveProfile = async () => {
    if (!editName.trim()) {
      showToast('昵称不能为空')
      return
    }
    setSaving(true)
    try {
      const res = await userApi.updateProfile({ name: editName.trim() })
      if (res.user) {
        localStorage.setItem('edu_user', JSON.stringify(res.user))
        await fetchProfile()
      }
      showToast('修改成功')
      setEditing(false)
    } catch (err) {
      showToast(err.message || '修改失败')
    } finally {
      setSaving(false)
    }
  }

  // 检查更新
  const handleCheckUpdate = async () => {
    if (checking) return
    showToast('正在检查更新...')
    await checkAndShow()
  }

  // 监听检查结果
  useEffect(() => {
    const handleResult = (e) => {
      const { hasUpdate: found, currentVersion: cv, error, message, version } = e.detail
      if (error) {
        showToast(message || '检查失败，请稍后再试')
      } else if (found) {
        // UpdateDialog 自动弹出
      } else {
        showToast(`当前已是最新版本 v${cv || currentVersion}`)
      }
    }
    window.addEventListener(CHECK_RESULT_EVENT, handleResult)
    return () => window.removeEventListener(CHECK_RESULT_EVENT, handleResult)
  }, [showToast, currentVersion])

  useEffect(() => {
    if (isLoggedIn) fetchProfile()
  }, [isLoggedIn, fetchProfile])

  // 未登录状态
  if (!isLoggedIn) {
    return (
      <div className="flex flex-col h-full" style={{ background: bgPrimary }}>
        {/* 头部背景 */}
        <div className="h-36 flex flex-col items-center justify-center relative safe-area-top" style={{ background: 'linear-gradient(135deg,#6C63FF 0%, #a29bfe 60%, #6C63FF 100%)' }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
            <i className="fa-solid fa-user text-4xl text-white" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">欢迎来到说灵灵</h2>
          <p className="text-xs text-white opacity-80">登录后开启你的学习之旅</p>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar pt-4" style={{ paddingBottom: '72px' }}>
          {/* 登录按钮 */}
          <div className="mx-4 mb-6">
            <button
              onClick={() => navigate('login')}
              className="w-full h-12 rounded-xl text-base font-medium text-white transition-transform active:scale-98 border-none cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #6C63FF, #8B5CF6)' }}
            >
              立即登录 / 注册
            </button>
          </div>

          {/* 功能预览 */}
          <div className="mx-4 mb-3 rounded-2xl p-5" style={{ background: bgWhite, boxShadow: shadowColor }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: textPrimary }}>登录后可享受</h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { icon: 'fa-book-open', label: '学习记录' },
                { icon: 'fa-heart', label: '收藏课程' },
                { icon: 'fa-certificate', label: '获得证书' },
                { icon: 'fa-book-open', label: '知识浏览' },
              ].map(item => (
                <div key={item.label} className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: progressBg }}>
                    <i className={`fa-solid ${item.icon}`} style={{ color: '#6C63FF' }} />
                  </div>
                  <span className="text-[10px] text-center" style={{ color: textSecondary }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 设置入口 */}
          <div className="mx-4 rounded-2xl overflow-hidden" style={{ background: bgWhite, boxShadow: shadowColor }}>
            <button
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-transparent border-none cursor-pointer text-left transition-colors"
              onClick={() => navigate('settings')}
            >
              <i className="fa-solid fa-gear text-base w-5 text-center" style={{ color: textMuted }} />
              <span className="flex-1 text-sm" style={{ color: textPrimary }}>设置</span>
              <i className="fa-solid fa-chevron-right text-xs" style={{ color: textMuted }} />
            </button>
            <div style={{ height: '1px', background: borderColorLight, marginLeft: '44px' }} />
            <button
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-transparent border-none cursor-pointer text-left transition-colors"
              onClick={handleCheckUpdate}
              disabled={checking}
            >
              <i className="fa-solid fa-arrow-up-from-bracket text-base w-5 text-center" style={{ color: '#00B894' }} />
              <span className="flex-1 text-sm" style={{ color: textPrimary }}>检查更新</span>
              {checking ? (
                <i className="fa-solid fa-spinner fa-spin text-xs" style={{ color: textMuted }} />
              ) : hasUpdate ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-lg text-white" style={{ background: '#FF6B6B' }}>新</span>
              ) : (
                <span className="text-[11px]" style={{ color: textMuted }}>v{currentVersion}</span>
              )}
            </button>
          </div>

          <div className="h-5" />
        </div>
      </div>
    )
  }

  // 已登录状态
  return (
    <div className="flex flex-col h-full" style={{ background: bgPrimary }}>
      {/* 头部渐变背景 + 头像区 */}
      <div className="relative safe-area-top">
        <div className="h-28" style={{ background: 'linear-gradient(135deg,#6C63FF,#a29bfe)' }} />
        <div className="px-4 pb-4" style={{ marginTop: '-48px' }}>
          <div className="flex flex-col items-center">
            {/* 头像 */}
            <div className="relative mb-2">
              <img
                src={user?.avatar || `https://api.dicebear.com/8.x/avataaars/svg?seed=${user?.phone || 'default'}`}
                className="w-20 h-20 rounded-full"
                style={{ border: '4px solid ' + bgWhite, boxShadow: '0 4px 16px rgba(108,99,255,0.25)' }}
                alt="avatar"
              />
              <button
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center border-none cursor-pointer transition-transform hover:scale-110"
                style={{ background: '#6C63FF', boxShadow: '0 2px 8px rgba(108,99,255,0.4)' }}
                onClick={startEdit}
              >
                <i className="fa-solid fa-pen text-white text-[10px]" />
              </button>
            </div>
            <h2 className="text-lg font-black mb-0.5" style={{ color: textPrimary }}>{user?.name || '说灵灵用户'}</h2>
            <p className="text-[11px]" style={{ color: textMuted }}>{user?.phone || ''}</p>
          </div>
        </div>
      </div>

      {/* 数据统计 */}
      <div className="mx-4 mb-3 rounded-2xl flex items-center" style={{ background: bgWhite, boxShadow: shadowColor }}>
        {[
          { val: user?.streak ?? 0,        label: '连续打卡' },
          { val: 3,                          label: '在学课程' },
          { val: 1,                          label: '已完成'   },
          { val: (user?.totalHours ?? 0) + 'h', label: '累计学习' },
        ].map((s, i) => (
          <div key={i} className="flex-1 flex flex-col items-center py-3" style={{ borderRight: i < 3 ? `1px solid ${borderColorLight}` : 'none' }}>
            <span className="text-lg font-black" style={{ color: '#6C63FF' }}>{s.val}</span>
            <span className="text-[10px] mt-0.5" style={{ color: textSecondary }}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar pt-1" style={{ paddingBottom: '72px' }}>

        {/* 菜单组 */}
        {MENU_GROUPS.map((group, gi) => (
          <div key={gi} className="mx-4 mb-2.5 rounded-2xl overflow-hidden" style={{ background: bgWhite, boxShadow: shadowColor }}>
            {group.map((item, ii) => {
              const badge = item.action === '__check_update__' && hasUpdate ? '新' : item.badge
              return (
                <button key={ii}
                        className="w-full flex items-center gap-3 px-4 py-3 bg-transparent border-none cursor-pointer text-left transition-colors hover:bg-[rgba(108,99,255,0.04)]"
                        style={{ borderBottom: ii < group.length - 1 ? `1px solid ${borderColorLight}` : 'none' }}
                        onClick={() => {
                          if (item.action === '__check_update__') {
                            handleCheckUpdate()
                          } else {
                            item.toPage ? navigate(item.toPage) : showToast(item.action)
                          }
                        }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ background: item.color === 'inherit' ? (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9') : item.color + '18' }}>
                    <i className={`fa-${item.regular ? 'regular' : 'solid'} ${item.icon} text-sm`}
                       style={{ color: item.color === 'inherit' ? textSecondary : item.color }} />
                  </div>
                  <span className="flex-1 text-sm" style={{ color: textPrimary }}>{item.label}</span>
                  {item.count > 0 && (
                    <span className="text-xs mr-2" style={{ color: textSecondary }}>{item.count}</span>
                  )}
                  {badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-lg text-white mr-1" style={{ background: '#FF6B6B' }}>{badge}</span>
                  )}
                  {item.tag && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md mr-1" style={{ background: isDark ? '#3d3d5a' : '#FFE8D6', color: '#E17055' }}>{item.tag}</span>
                  )}
                  <i className="fa-solid fa-chevron-right text-xs" style={{ color: textMuted }} />
                </button>
              )
            })}
          </div>
        ))}

        {/* 编辑资料弹窗 */}
        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
            <div className="w-full max-w-sm rounded-2xl p-6 mx-4" style={{ background: bgWhite }}>
              <div className="flex items-center justify-between mb-6">
                <span className="text-lg font-bold" style={{ color: textPrimary }}>编辑资料</span>
                <button onClick={() => setEditing(false)} className="w-8 h-8 flex items-center justify-center rounded-full border-none cursor-pointer transition-colors"
                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                  <i className="fa-solid fa-xmark" style={{ color: textMuted }} />
                </button>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>昵称</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  maxLength={20}
                  className="w-full h-12 px-4 rounded-xl border outline-none text-sm transition-all"
                  style={{ borderColor: borderColorLight, background: progressBg, color: textPrimary }}
                  onFocus={e => e.target.style.borderColor = '#6C63FF'}
                  onBlur={e => e.target.style.borderColor = borderColorLight}
                  placeholder="请输入昵称"
                />
              </div>
              <button
                onClick={saveProfile}
                disabled={saving}
                className="w-full h-12 rounded-xl text-base font-medium text-white border-none cursor-pointer transition-transform disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #6C63FF, #8B5CF6)' }}
              >
                {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : '保存'}
              </button>
            </div>
          </div>
        )}

        {/* 退出登录 */}
        <button className="mx-4 w-[calc(100%-32px)] h-11 rounded-2xl border text-sm font-bold cursor-pointer bg-transparent transition-all hover:bg-[rgba(255,107,107,0.08)] active:scale-[0.98]"
                style={{ borderColor: isDark ? 'rgba(255,107,107,0.3)' : 'rgba(255,107,107,0.2)', color: '#FF6B6B' }}
                onClick={logout}>
          退出登录
        </button>

        <div className="h-5" />
      </div>
    </div>
  )
}
